import { useState } from 'react'

import {
  normalizeList,
  orchestrator,
  readManualTransfer,
  readManualTransferOptions,
  useCreateBatch,
  usePreflightBatch,
  useReleaseHandoffPoint,
  useStartManualTransfer,
  useStopBatch,
  type ApiError,
} from '@ctcells/api'
import type { Batch, ModuleSnapshot, PreflightResult, Recipe } from '@ctcells/contract'
import { useStripChoiceStore } from '@ctcells/core'
import { serverText, t } from '@ctcells/i18n'

import { AddModal } from '../atoms/AddModal.tsx'
import { Button } from '../atoms/Button.tsx'
import { Checkbox } from '../atoms/Checkbox.tsx'
import { Field } from '../atoms/Field.tsx'
import { useSubmitValidation } from '../atoms/useSubmitValidation.ts'
import { TextInput } from '../atoms/TextInput.tsx'
import { ValueRows } from '../atoms/ValueRows.tsx'
import type { ActionGate } from '../screens/access.ts'
import { quickFailureText } from '../screens/batches/quickResetPorts.ts'
import { FormSection, ModalFooter } from '../screens/formSections.tsx'
import { historyTargetText } from '../screens/format.ts'

import { runHeldTrayRecreate, type HeldTrayPorts, type HeldTrayResult } from './heldTrayRecreate.ts'

/**
 * 시작 창 · 6축 「대기 아님(HANDOFF)」 줄의 **[작업 중단 후 내려놓기]** — 흐름은 `heldTrayRecreate.ts` 머리말.
 *
 * 🔴 이 단추는 **이 작업을 중단한다** — 그래서 확인 창이 그것을 먼저 말하고, 사유 · 현장 확인(로봇 팔 주변 · 쥔 트레이)을 받는다.
 *    끊은 뒤에는 같은 레시피 · 시료 · 웰로 **다시 만든다** — 결과는 새 작업이고 창은 그 작업으로 점검을 잇는다(`onDone`).
 * ⚠ `place_held` 는 계약상 `physically_checked: true` 가 필수다(`ManualTransferRequest`) — 그 확인이 이 창의 체크다.
 */
export function HeldTrayFix({
  batchId,
  module,
  gate,
  onRun,
}: {
  batchId: string
  module: ModuleSnapshot
  gate: ActionGate
  /** 흐름을 그 줄에서 돌린다 — 진행 문구 · 결과 처리는 부르는 쪽(`StartPreflightActions`) */
  onRun: (send: (onStatus: (text: string) => void) => Promise<HeldTrayResult>) => void
}) {
  const ports = useHeldTrayPorts()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const validation = useSubmitValidation((value: string) => ({
    reason: value.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))
  const reasonField = validation.field('reason')
  const [checked, setChecked] = useState(false)

  const close = () => {
    setOpen(false)
    setReason('')
    validation.reset()
    setChecked(false)
  }
  const submitGate: ActionGate = gate.disabled
    ? gate
    : !checked
      ? { disabled: true, disabledReason: t('reason.physicalCheckRequired') }
      : { disabled: false }

  return (
    <>
      <Button onClick={() => setOpen(true)} size="pill" {...gate}>
        {/* 점검 표의 좁은 칸에서도 한 줄로 선다(`startPreflightActions.css` `.pffix__nowrap`) */}
        <span className="pffix__nowrap">{t('pfHeld.button')}</span>
      </Button>
      {open ? (
        <AddModal size="md" onClose={close} title={t('pfHeld.action')}>
          <FormSection>
            <p className="small" role="note">
              {t('pfHeld.notice')}
            </p>
            <ValueRows
              rows={[
                { key: 'batch', label: t('column.batch'), value: batchId },
                {
                  key: 'module',
                  label: t('column.module'),
                  value: historyTargetText(module.module_id),
                },
              ]}
            />
            <Field {...reasonField.fieldProps} label={t('field.reason')} required>
              <TextInput
                {...reasonField.inputProps}
                onChange={(next) => {
                  setReason(next)
                  validation.change('reason', next)
                }}
                value={reason}
                wide
              />
            </Field>
            <Checkbox checked={checked} label={t('pfHeld.physicalCheck')} onChange={setChecked} />
          </FormSection>
          <ModalFooter>
            <Button onClick={close} size="pill">
              {t('action.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (submitGate.disabled) return
                if (
                  !validation.validate(
                    reason,
                    document.getElementById(reasonField.inputProps.id)?.parentElement ?? null,
                  )
                )
                  return
                const said = reason.trim()
                close()
                onRun((onStatus) =>
                  runHeldTrayRecreate(ports, onStatus, {
                    batchId,
                    robotModuleId: module.module_id,
                    reason: said,
                  }),
                )
              }}
              size="pill"
              variant="danger"
              {...submitGate}
            >
              {t('pfHeld.action')}
            </Button>
          </ModalFooter>
        </AddModal>
      ) : null}
    </>
  )
}

/** 실제 창구 — 명령은 기존 명령 훅의 `mutateAsync`(본문 · `command_id` · 제어락 헤더 · 무효화가 그 훅에서 온다). 조회는 단발 */
function useHeldTrayPorts(): HeldTrayPorts {
  const stop = useStopBatch()
  const place = useStartManualTransfer()
  const createBatch = useCreateBatch()
  const preflight = usePreflightBatch()
  const releaseHandoff = useReleaseHandoffPoint()
  const choose = useStripChoiceStore((state) => state.choose)
  return {
    readBatch: (id) => orchestrator.request<Batch>({ method: 'GET', path: `/batches/${id}` }),
    readRecipe: (recipeId) =>
      orchestrator.request<Recipe>({ method: 'GET', path: `/recipes/${recipeId}` }),
    stopBatch: async (id, reason) => {
      await stop.mutateAsync({ batchId: id, reason })
    },
    readPlaceOptions: readManualTransferOptions,
    placeHeld: (destPointId, reason) =>
      place.mutateAsync({ kind: 'place_held', destPointId, reason }),
    readManualTransfer,
    listModules: async () =>
      normalizeList<ModuleSnapshot>(
        await orchestrator.request({ method: 'GET', path: '/modules', query: {} }),
      ).items,
    createBatch: (source) =>
      createBatch.mutateAsync({
        recipeId: source.recipeId,
        sampleIds: source.assignments.map((one) => one.sample_id),
        assignments: source.assignments,
      }),
    choose,
    preflight: (id): Promise<PreflightResult> => preflight.mutateAsync({ batchId: id }),
    releaseHandoff: async (moduleId, pointId) => {
      await releaseHandoff.mutateAsync({
        moduleId,
        pointId,
        reason: serverText('pfHeld.handoffReason', { point: pointId }),
      })
    },
    sleep: (ms) =>
      new Promise((resolve) => {
        setTimeout(resolve, ms)
      }),
    now: () => Date.now(),
    failure: (error) => quickFailureText(error as ApiError),
  }
}
